import React, { useCallback, useMemo, useState } from 'react';
import useOneWayInServiceProviders from '../../hooks/use-one-way-in-service-providers';
import { useTranslation } from 'react-i18next';
import useDocumentTitle from 'src/hooks/use-document-title';
import { LoadingPage } from 'src/components/LoadingPage/LoadingPage';
import { Link } from 'react-router-dom';
import { Checkbox, DateInput, InputField } from '@innovationnorway/component-library';
import { Alert, Button, Heading, Tag, Icon, Tabs } from '@innovationnorway/cl-react';
import DocumentTitle from 'src/components/DocumentTitle/DocumentTitle';
import Page from 'src/components/Page/Page';
import '../../styles/OneWayInStyle.scss';
import {
  OneWayInFeedbackDto,
  OneWayInFormCollection,
  OneWayInOpportunitySimpleV2Dto,
  Skjemainfo,
} from 'src/api/v2';
import { SelectWrapper } from 'src/components/Form/SelectWrapper';
import OneWayInReviewListItem from './OneWayInReviewListItem';
import { UseInfiniteQueryResult, UseQueryResult } from '@tanstack/react-query';
import { Counties } from '../../constants/counties';
import { MINSIDE_REFERRALPARTY_FIRST_LINE_FILTER } from '../../constants/firstLineReferralParty';
import { ONE_WAY_IN } from '../..';
import OneWayInNoteListItem from './OneWayInNoteListItem';
import useGeonorge from '../../hooks/use-geonorge';
type OrderFromOldToNew = {
  init: boolean;
  set: (prevValue: boolean) => void;
};
type Props = {
  listQuery: UseInfiniteQueryResult<OneWayInFormCollection, unknown>;
  feedbackListQuery: UseQueryResult<OneWayInFeedbackDto[], unknown>;
  greenPlatformOpportunities?: OneWayInOpportunitySimpleV2Dto[];
  fromList: string[];
  countyView?: boolean;
  setFromDate?: (froMDate?: Date) => void;
  setToDate?: (toDate?: Date) => void;
  orderFromOldToNew?: OrderFromOldToNew;
};
export type FeedbackObject = {
  opportunityId: string;
  wasCorrectlyReferred: boolean;
  comment?: string;
};
export type ReviewDto = Skjemainfo & {
  recipients: string[] | undefined;
  feedback?: FeedbackObject;
  county: string | undefined;
};
type FeedbackResolvedFilter = 'missing' | 'correct' | 'incorrect' | 'notset';
type TabNames = 'GreenPlatform' | 'Actor';
const OneWayInReviewOpportunities: React.FC<Props> = ({
  listQuery,
  feedbackListQuery,
  greenPlatformOpportunities,
  fromList,
  countyView = false,
  setFromDate,
  setToDate,
  orderFromOldToNew,
}) => {
  const { serviceProviders } = useOneWayInServiceProviders();
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { t: tCommon } = useTranslation();
  const { getMunicipalityNameByNumber } = useGeonorge();
  const [searchQuery, setSearchQuery] = useState('');
  const [municipalityFilter, setMunicipalityFilter] = useState('');
  const [feedbackStatusFilter, setFeedbackStatusFilter] = useState<FeedbackResolvedFilter>('notset');
  const hasGreenPlatform =
    countyView == false && greenPlatformOpportunities != undefined && greenPlatformOpportunities.length > 0;
  const [selectedTab, setSelectedTab] = useState<TabNames>(() => {
    if (hasGreenPlatform) return 'GreenPlatform';
    return 'Actor';
  });
  const { documentTitle } = useDocumentTitle(tOneWayIn('opportunities'));
  const feedbackFilterOptions = [
    {
      text: tCommon('none'),
      value: 'notset',
    },
    {
      text: tOneWayIn('feedbackFilter.Missing'),
      value: 'missing',
    },
    {
      text: tOneWayIn('feedbackFilter.Correct'),
      value: 'correct',
    },
    {
      text: tOneWayIn('feedbackFilter.Incorrect'),
      value: 'incorrect',
    },
  ];
  const feedbackList = useMemo(() => {
    if (!feedbackListQuery.data) {
      return [] as FeedbackObject[];
    }
    return feedbackListQuery.data.map(
      (feedback) =>
        ({
          opportunityId: feedback.oneWayInOpportunityD365Id,
          wasCorrectlyReferred: feedback.wasCorrectlyReferred,
          comment: feedback.comment,
        }) satisfies FeedbackObject,
    );
    // dataUpdatedAt is to ensure we are updating. May be unnecessary but leaving it here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackListQuery.data, feedbackListQuery.dataUpdatedAt]);
  // Service Providers are clusters that offer OneWayInServices
  const filterServiceProvidersWithMunicipality = useCallback(
    (municipalityNumber: string) => {
      const serviceProvider = serviceProviders?.filter((sp) =>
        sp.municipalities.includes(municipalityNumber),
      );
      return serviceProvider;
    },
    [serviceProviders],
  );
  const getRecipients = useCallback(
    (opportunity: Skjemainfo) => {
      if (!opportunity.henvisningsPartnere) {
        return undefined;
      }
      const firstLineServiceProviders: string[] = [];
      if (
        opportunity.henvisningsPartnere.some(
          (partner) => partner.id === MINSIDE_REFERRALPARTY_FIRST_LINE_FILTER,
        )
      ) {
        // Add all first line service provider names for the municipality
        const firstLineProviders = filterServiceProvidersWithMunicipality(opportunity.prosjektKommune || '');
        if (firstLineProviders) {
          firstLineServiceProviders.push(...firstLineProviders.map((sp) => sp.name ?? ''));
        }
      }
      const otherProviders = opportunity.henvisningsPartnere.map((p) => p.navn ?? '');
      const combinedProviders = [...firstLineServiceProviders, ...otherProviders].filter(
        (provider) => provider.length,
      );
      const uniqueProviders = [...new Set(combinedProviders)];
      return uniqueProviders;
    },
    [filterServiceProvidersWithMunicipality],
  );
  const filterOpportunities = useCallback(
    (opportunities: ReviewDto[]) => {
      let filteredOpportunities: ReviewDto[] = [...opportunities];
      if (municipalityFilter.length > 0) {
        filteredOpportunities = filteredOpportunities.filter(
          (opportunity) => opportunity.prosjektKommune?.toLowerCase() === municipalityFilter.toLowerCase(),
        );
      }
      if (feedbackStatusFilter === 'correct') {
        filteredOpportunities = filteredOpportunities.filter(
          (opportunity) => opportunity.feedback?.wasCorrectlyReferred,
        );
      } else if (feedbackStatusFilter === 'incorrect') {
        filteredOpportunities = filteredOpportunities.filter(
          (opportunity) => opportunity.feedback?.wasCorrectlyReferred === false,
        );
      } else if (feedbackStatusFilter === 'missing') {
        filteredOpportunities = filteredOpportunities.filter(
          (opportunity) => opportunity.feedback === undefined,
        );
      }
      filteredOpportunities = filteredOpportunities.filter(
        (opportunity) =>
          opportunity.tittel?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          opportunity.prosjektKommune?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          opportunity.recipients?.find((recipient) =>
            recipient.toLowerCase().includes(searchQuery.toLowerCase()),
          ),
      );
      return filteredOpportunities;
    },
    [feedbackStatusFilter, municipalityFilter, searchQuery],
  );
  const { opportunityList, municipalityOptions, total, notReviewed, sentCorrectly, sentIncorrectly } =
    useMemo(() => {
      if (!listQuery.data?.pages) {
        return {
          opportunityList: [] as ReviewDto[],
          municipalityOptions: [] as {
            text: string;
            value: string;
          }[],
          total: 0,
          notReviewed: 0,
          sentCorrectly: 0,
          sentIncorrectly: 0,
        };
      }
      const opportunities: ReviewDto[] = listQuery.data?.pages
        .flatMap((data) => data.forms)
        .map(
          (opportunity) =>
            ({
              ...opportunity,
              county:
                Counties.find((county) =>
                  county.municipalities.some((municipality) => municipality === opportunity.prosjektKommune),
                )?.countyName || undefined,
              recipients: getRecipients(opportunity),
              feedback: feedbackList.find((resolved) => resolved.opportunityId === opportunity.id),
            }) satisfies ReviewDto,
        );
      const filteredOpportunities = filterOpportunities(opportunities);
      const municipalityOptions = opportunities
        .map((opportunity) => opportunity.prosjektKommune)
        .filter((value, index, array) => array.indexOf(value) === index)
        .filter((municipality) => municipality)
        .map((municipality) => ({
          text: getMunicipalityNameByNumber(municipality) || '',
          value: municipality?.toLowerCase() || '',
        }))
        .sort((a, b) => {
          if (a.text < b.text) return -1;
          if (a.text > b.text) return 1;
          return 0;
        });
      const counts = opportunities.reduce(
        (acc, opportunity) => {
          if (opportunity.feedback === undefined) {
            acc.notReviewed += 1;
          } else if (opportunity.feedback.wasCorrectlyReferred) {
            acc.sentCorrectly += 1;
          } else {
            acc.sentIncorrectly += 1;
          }
          return acc;
        },
        { notReviewed: 0, sentCorrectly: 0, sentIncorrectly: 0 },
      );
      return {
        opportunityList: filteredOpportunities,
        municipalityOptions: [
          {
            text: tCommon('none'),
            value: '',
          },
          ...municipalityOptions,
        ],
        total: opportunities.length,
        ...counts,
      };
    }, [
      listQuery.data?.pages,
      filterOpportunities,
      tCommon,
      getRecipients,
      getMunicipalityNameByNumber,
      feedbackList,
    ]);
  return (
    <>
      <DocumentTitle title={documentTitle}>
        <Page>
          <Heading level={1}>{tOneWayIn('opportunities')}</Heading>
          <p className="font-size--large font">
            For at Innovasjon Norge fortløpende skal kunne øke treffsikkerheten når kunder henvises til
            aktører i virkemiddelapparatet, er det behov for bistand fra representanter tilknyttet relevante
            virkemiddelaktører. Innovasjon Norge vil i den forbindelse kunne ha behov for å gi tilgang til
            taushetsbelagt informasjon (for eksempel oversikter over henvendelser som ennå ikke er henvist,
            eller henvist til andre enn den enkelte aktør). Denne visningen er et eksempel på dette.
            Representantene som gis tilgang til taushetsbelagt informasjon vil ha taushetsplikt i henhold til{' '}
            <Link
              to="https://lovdata.no/dokument/NL/lov/2003-12-19-130"
              target="_blank"
              rel="noopener noreferrer"
            >
              lov om Innovasjon Norge § 27.
            </Link>
          </p>
          {fromList.length > 0 && (
            <div className="margin-top--4">{tOneWayIn('opportunityDetails.MunicipalitiesList')}</div>
          )}
          {fromList.sort().length > 0 && (
            <div className="opportunities__municipalityList">
              {fromList.sort().map((municipalityName) => (
                <Tag
                  className="opportunities__municipalityList--tags"
                  key={municipalityName}
                  data-color={'info' as const}
                >
                  {municipalityName || ''}
                </Tag>
              ))}
            </div>
          )}
          {countyView && (
            <>
              <div className="margin-top--2">
                <Link to={ONE_WAY_IN.BASE}>
                  {tOneWayIn('goToMunicipalityView')} <Icon prefix="fasr" iconName="chevron-right" />
                </Link>
              </div>
              <div className={'margin-top--2'}>
                <Alert data-color="info">{tOneWayIn('countyView.NoteOpportunitiesCanOnlyBeHandled')}</Alert>
              </div>
            </>
          )}
          <div className="margin-top--4">
            {hasGreenPlatform && (
              <Tabs value={selectedTab} className="margin-top--6">
                <Tabs.List className="margin-bottom--2">
                  <Tabs.Tab
                    value="GreenPlatform"
                    onClick={() => setSelectedTab('GreenPlatform')}
                    aria-label={tOneWayIn('tabs.greenPlatform')}
                  >
                    {tOneWayIn('tabs.greenPlatform')}
                  </Tabs.Tab>
                  <Tabs.Tab
                    value="Actor"
                    onClick={() => setSelectedTab('Actor')}
                    aria-label={tOneWayIn('tabs.actor')}
                  >
                    {tOneWayIn('tabs.actor')}
                  </Tabs.Tab>
                </Tabs.List>
              </Tabs>
            )}
            <InputField
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={tCommon('search')}
              className="width--60"
            />
          </div>
          <div className="display--flex justify-content--space-between align-items--center">
            <div>
              <Button
                type="button"
                variant="secondary"
                disabled={!listQuery.hasNextPage || listQuery.isFetching}
                onClick={() => listQuery.fetchNextPage()}
              >
                {tOneWayIn('getMoreOpportunities')}
              </Button>
            </div>
            <SelectWrapper
              label={tOneWayIn('filterByMunicipality')}
              options={municipalityOptions}
              onChange={(e) => setMunicipalityFilter(e.target.value)}
            />
            <SelectWrapper
              label={tOneWayIn('feedbackFilter.Title')}
              options={feedbackFilterOptions}
              onChange={(e) => setFeedbackStatusFilter(e.target.value as FeedbackResolvedFilter)}
            />
          </div>
          <div className="margin-top--2 margin-bottom--2 display--flex justify-content--space-between align-items--center">
            {orderFromOldToNew && (
              <div>
                <Checkbox
                  name="orderFromOldToNew"
                  options={[{ text: tCommon('oldestToNewest'), value: '', selected: orderFromOldToNew.init }]}
                  onChange={(e) => orderFromOldToNew.set(e.currentTarget.checked)}
                />
              </div>
            )}
            {setFromDate && (
              <div>
                <DateInput
                  name="fromDate"
                  label={`${tCommon('from')} ${tCommon('date').toLowerCase()}`}
                  onChange={(event) => {
                    const { value } = event.target;
                    const endDate = value ? new Date(value) : undefined;
                    setFromDate(endDate);
                  }}
                />
              </div>
            )}
            {setToDate && (
              <div>
                <DateInput
                  name="toDate"
                  label={`${tCommon('to')} ${tCommon('date').toLowerCase()}`}
                  onChange={(event) => {
                    const { value } = event.target;
                    const toDate = value ? new Date(value) : undefined;
                    setToDate(toDate);
                  }}
                />
              </div>
            )}
          </div>
          <div>
            {listQuery.isLoading && <LoadingPage />}
            {!listQuery.isLoading && listQuery.isError && (
              <Alert data-color="danger" role="alert">
                {tOneWayIn('errorCouldNotGetAllForms')}
              </Alert>
            )}
            {!!opportunityList.length && (
              <div className="display--flex margin-top--1 margin-bottom--1">
                <div>
                  <Icon
                    prefix="fasr"
                    iconName="check-circle"
                    size="l"
                    color="#2fcc72"
                    className="margin-right--1"
                  />{' '}
                  {sentCorrectly}
                </div>
                <div className="margin-left--3">
                  <Icon prefix="fasr" iconName="times" size="l" color="#ed0000" className="margin-right--1" />{' '}
                  {sentIncorrectly}
                </div>
                <div className="margin-left--3">
                  <Icon prefix="fasr" iconName="square-info" size="xl" className="margin-right--1" />{' '}
                  {notReviewed}
                </div>
              </div>
            )}
            {!listQuery.isLoading && total > 0 && (
              <div>
                {`${tOneWayIn('services.Displaying')} ${opportunityList.length} ${tOneWayIn('services.OfTotal')} ${total}.`}
              </div>
            )}
            {!listQuery.isLoading && !listQuery.isError && opportunityList.length === 0 && (
              <div className="margin-top--2">{tOneWayIn('opportunityDetails.FoundNoOpportunities')}</div>
            )}
            {!listQuery.isLoading &&
              selectedTab === 'Actor' &&
              opportunityList.map((opportunity) => {
                return <OneWayInReviewListItem key={opportunity.id} opportunity={opportunity} />;
              })}
            {!listQuery.isLoading &&
              selectedTab === 'GreenPlatform' &&
              greenPlatformOpportunities?.map((opportunity) => (
                <OneWayInNoteListItem key={opportunity.id} opportunity={opportunity} />
              ))}
          </div>
        </Page>
      </DocumentTitle>
    </>
  );
};
export default OneWayInReviewOpportunities;
